import React, { useEffect, useRef, useState, useMemo } from "react";
import { Tooltip } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import AnimatedMainContainer from "../../components/AnimatedMainContainer";
import { useToast } from "../../hooks/toast";
import * as Yup from "yup";
import getValidationErrors from "../../utils/getValidationErrors";
import api from "../../service";
import { useCallback } from "react";
import FormButton from "../../components/FormButton";
import { Form } from "@unform/web";
import { validateSingleFieldOnBlur } from "../../utils/formValidations";
import { base64ToBlob, downloadBlob } from "../../utils/file";
import { format } from "date-fns";

import {
  Container,
  FormContainer,
  SearchFormContainer,
  TitleContainer,
  Title,
  FieldsContainer,
  Horizontal,
  FilterActionsContainer,
  ExportContainer,
  ActionContainer,
  BadgeContainer,
  DelimiterContainer,
} from "./styles";
import SelectWithLabel from "../../components/SelectWithLabel";
import InputWithHorizontalLabel from "../../components/InputWithHorizontalLabel";
import TextareaWithLabel from "../../components/TextareaWithLabel";
import Input from "../../components/Input";
import Badge from "../../components/Badge";
import ConfirmationModal from "../../components/ConfirmationModal";
import Table from "../../components/Table";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import { useLoading } from "../../hooks/loading";
import credentialTypesConstants from "../../constants/credentialTypes";

import {
  AiOutlineSearch,
} from "react-icons/ai";

const BonusCredential = () => {
  const { token } = useSelector((state) => state.auth);
  const formRef = useRef(null);
  const intl = useIntl();
  const history = useHistory();
  const { addToast } = useToast();
  const { handleLoading } = useLoading();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventCode, setSelectedEventCode] = useState();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState();
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState();
  const [allowedCredentialTypes, setAllowedCredentialTypes] = useState(null);
  const [serviceProviders, setServiceProviders] = useState([]);
  const [laborUnions, setLaborUnions] = useState([]);
  const [bonusCredentialId, setBonusCredentialId] = useState();
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const credentialTypes = credentialTypesConstants();
  const filteredCredentialTypes = allowedCredentialTypes
    ? credentialTypes.filter((item) => allowedCredentialTypes.includes(item.value))
    : [];

  const schema = Yup.object().shape({
    event: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
    account: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
    opportunity: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
    serviceProvider: Yup.string().nullable(),
    quantity: Yup.string()
      .required(intl.formatMessage({ id: "error.field_required" }))
      .matches(
        /^(^$|([0-9]*))$/g,
        intl.formatMessage({ id: "error.need_to_be_number" })
      )
      .test(
        "min-value",
        intl.formatMessage(
          { id: "error.min_value" },
          { value: 1 }
        ),
        () => {
          const value = parseInt(formRef.current.getFieldValue("quantity"));

          if (value === 0) {
            return false;
          }

          return (!value || value >= 1);
        }
      )
      .test(
        "max-value",
        intl.formatMessage(
          { id: "error.max_value" },
          { value: 1000 }
        ),
        () => {
          const value = parseInt(formRef.current.getFieldValue("quantity"));

          return (!value || value <= 1000);
        }
      ),
    credentialType: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
    // INFORMACP-4819: o campo `reason` e exibido como "Observacao" e passou a ser limitado a 200
    // caracteres; `laborUnion` e exibido como "Motivo" e passou a ser obrigatorio.
    reason: Yup.string()
      .max(200, intl.formatMessage({ id: "error.max_length" }, { length: 200 }))
      .required(intl.formatMessage({ id: "error.field_required" })),
    laborUnion: Yup.array()
      .nullable()
      .min(1, intl.formatMessage({ id: "error.field_required" }))
      .required(intl.formatMessage({ id: "error.field_required" })),
  });

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        setLoading(true);
        if (loading) {
          return;
        }

        await schema.validate(data, { abortEarly: false });

        api
          .post(
            `/bonus-credential`,
            {
              eventId: data.event,
              accountId: data.account,
              opportunityId: data.opportunity,
              serviceProviderId: data.serviceProvider,
              quantity: data.quantity,
              credentialType: credentialTypes.find(
                  (item) => item.value === data.credentialType)?.value,
              reason: data.reason,
              laborUnionList: data.laborUnion,
            },
            {
              headers: {
                "Content-Type": "application/json",
                Accept: "*/*",
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((result) => {
            addToast({
              type: "success",
              title: intl.formatMessage({
                id: "success",
              }),
              description: intl.formatMessage({
                id: "success.bonus_credential_released",
              }),
            });
            resetForm();
          })
          .catch((err) => {
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            })
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (err) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }
      }
    },
    [loading, schema, token]
  );

  useEffect(() => {
    handleLoading(true);
    api
      .get(`/bonus-credential/labor-union`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((result) => {
        setLaborUnions(result.data.laborUnionOutput);
      })
      .catch((err) => {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
      })
      .finally(() => {
        handleLoading(false);
      });
  }, [addToast, intl, token, handleLoading]);

  useEffect(() => {
    handleLoading(true);
    api
      .get(`/admin/events`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((result) => {
        const events = result.data.output.map((event) => ({
          label: event.eventEditionName,
          value: event.idNumber,
          code: event.eventEditionCode,
        }));
        let eventsSort = [...events].sort((a, b) =>
          a.label < b.label ? -1 : a.label > b.label ? 1 : 0
        );

        setEvents(eventsSort);
      })
      .catch((err) => {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
      })
      .finally(() => {
        handleLoading(false);
      });
  }, [addToast, intl, token, handleLoading]);

  useEffect(() => {
    formRef.current.setFieldValue("account");
    if (selectedEventCode) {
      handleLoading(true);
      api
        .patch(
          "/admin/account/by-event-and-role",
          {
            eventEditionCode: selectedEventCode,
            roleType: "EXHIBITOR"
          },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setAccounts(response.data.accountDropdownOutputList)
        })
        .catch((err) => {
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          });
        })
        .finally(() => {
          handleLoading(false);
        });
    }
  }, [addToast, intl, selectedEventCode, token, handleLoading]);

  useEffect(() => {
    formRef.current.setFieldValue("opportunity");
    formRef.current.setFieldValue("credentialType");
    setAllowedCredentialTypes(null);
    if (selectedEventCode && selectedAccount) {
      handleLoading(true);
      api
        .patch(
          "/admin/opportunity/by-event-and-account",
            {
              eventEditionCode: selectedEventCode,
              accountId: selectedAccount,
              roleType: "EXHIBITOR"
            },
            {
              headers: {
                "Content-Type": "application/json",
                Accept: "*/*",
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((response) => {
            setOpportunities(response.data.opportunityDropdownOutputList)
          })
          .catch((err) => {
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            });
          })
          .finally(() => {
            handleLoading(false);
          });
    }
  }, [addToast, intl, selectedEventCode, selectedAccount, token, handleLoading]);

  useEffect(() => {
    formRef.current.setFieldValue("serviceProvider");
    if (selectedEventCode && selectedAccount && selectedOpportunity) {
      handleLoading(true);
      api
        .get(
          `/serviceprovider/by-opportunity?&opportunity_id=${selectedOpportunity}`,
            {
              headers: {
                "Content-Type": "application/json",
                Accept: "*/*",
                Authorization: `Bearer ${token}`,
              },
            }
          )
          .then((response) => {
            setServiceProviders(response.data.serviceProviderDropdownOutputList)
          })
          .catch((err) => {
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            });
          })
          .finally(() => {
            handleLoading(false);
          });
    }
  }, [
    addToast,
    intl,
    selectedEventCode,
    selectedAccount,
    selectedOpportunity,
    token,
    handleLoading
  ]);

  const validationBlur = (name) => {
    let errors = formRef.current.getErrors();
    validateSingleFieldOnBlur(errors, schema, name, formRef);
  };

  const fetchData = useCallback(
    async (
      page,
      size,
      search,
      resetCurrentPage
    ) => {
      setLoading(true);
      if (loading) return;

      if (resetCurrentPage) setCurrentPage(1);

      api
        .get(
          `/bonus-credential?page=${page}&per_page=${size}&search=${search}`,
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((result) => {
          setData(result.data.bonusCredentialOutput);
          setTotalRows(result.data.paginationInfo.totalElements);
        })
        .catch((err) =>
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          })
        )
        .finally(() => setLoading(false));
    },
    [addToast, intl, loading, perPage, search, token]
  );

  const handleSearch = (value) => {
    const lengthSearch = value.length;
    if (lengthSearch >= 3 || lengthSearch === 0) {
      setSearch(value);
      fetchData(0, perPage, value, true);
    }
  };

  const handlePageChange = (page) => {
    fetchData(page - 1, perPage, search, false);
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    fetchData(page - 1, newPerPage, search, false);
    setPerPage(newPerPage);
  };

  useEffect(() =>
    fetchData(currentPage - 1, perPage, search, false),
    []
  );

  const resetForm = () => {
    formRef.current.reset();
    formRef.current.setFieldValue("event");
    formRef.current.setFieldValue("account");
    formRef.current.setFieldValue("opportunity");
    formRef.current.setFieldValue("serviceProvider");
    formRef.current.setFieldValue("credentialType");
    formRef.current.setFieldValue("laborUnion");
    fetchData(0, perPage, search, true);
  };

  const showDeleteConfirmation = () => {
    setDeleteModalIsOpen(true);
  };

  const handleExclusion = () => {
    handleLoading(true);
    api
      .delete(`/bonus-credential/${bonusCredentialId}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((_) => {
        addToast({
          type: "success",
          title: intl.formatMessage({
            id: "success",
          }),
          description: intl.formatMessage({
            id: "success.bonus_credential_removed",
          }),
        });
        fetchData(0, perPage, search, true);
        setDeleteModalIsOpen(false);
      })
      .catch((err) => {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
      })
      .finally(() => {
        setBonusCredentialId(null);
        handleLoading(false);
      });
  };

  const exportBonusCredentials = useCallback(
    () => {
      try {
        setLoading(true);
        if (loading) {
          return;
        }

        api
          .get(
            `/bonus-credential/export?search=${search}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Accept: "*/*",
                  Authorization: `Bearer ${token}`,
                },
              }
          )
          .then((result) => {
            const { file, name } = result.data.exportOutput;
            var blob = base64ToBlob(file, "application/vnd.ms-excel");
            downloadBlob(blob, `${name}.xlsx`);
          })
          .catch((err) => {
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "api.error." + err.response.data.code,
              }),
            });
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (err) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);

          return;
        }
      }
    },
    [addToast, intl, loading, token, search]
  );

  const columns = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: "release_date" }),
        cell: (row) =>
          format(new Date(row.createdAt), "dd/MM/yyyy"),
      },
      {
        name: intl.formatMessage({ id: "id_of_who_released" }),
        selector: (row) => row.userEmailWhoReleased,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "event_name" }),
        selector: (row) => row.eventEditionName,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "exhibitor_name" }),
        selector: (row) => row.accountName,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "contract" }),
        selector: (row) => row.contractNumber,
        allowOverflow: true,
        wrap: true,
        minWidth: "150px",
      },
      {
        name: intl.formatMessage({ id: "provider_name" }),
        selector: (row) => row.serviceProviderName,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "type_of_credential" }),
        selector: (row) => row.credentialType,
        cell: (row) =>
          credentialTypes.filter((pt) => pt.value === row.credentialType)[0]?.label,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
      },
      {
        name: intl.formatMessage({ id: "quantity" }),
        selector: (row) => row.quantity,
        allowOverflow: true,
        wrap: true,
        minWidth: "120px",
      },
      // INFORMACP-4819: a tabela chamava o mesmo `laborUnion` de "Classificacao" enquanto o
      // formulario o chamava de "Sindicato". Ambos passam a ser "Motivo", e `reason` vira
      // "Observacao" nos dois lugares.
      {
        name: intl.formatMessage({ id: "reason" }),
        selector: (row) => row.laborUnion,
        allowOverflow: true,
        wrap: true,
        minWidth: "150px",
      },
      {
        name: intl.formatMessage({ id: "observation" }),
        selector: (row) => row.reason,
        allowOverflow: true,
        wrap: true,
        minWidth: "200px",
        // INFORMACP-4819 RF008: observação longa é truncada na coluna e exibida por inteiro no
        // hover.
        cell: (row) => {
          const text = row.reason || "";
          return (
            <Tooltip title={text} placement="topLeft">
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {text}
              </div>
            </Tooltip>
          );
        },
      },
      {
        cell: (row) => (
          <Badge
            onClick={() => {
                setBonusCredentialId(row.id);
                showDeleteConfirmation();
            }}
            color="#cc0000"
            text={intl.formatMessage({ id: "delete" })}
          />
        ),
        right: true,
      },
    ],
    [intl]
  );

  return (
    <AnimatedMainContainer
      breadcrumb={intl.formatMessage({ id: "breadcrumb.bonus_credentials" })}
    >
      <Container>
        <FormContainer>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <TitleContainer>
              <Title>
                <FormattedMessage id="bonus_credentials" />
              </Title>
              <ActionContainer>
                <BadgeContainer
                  onClick={() => history.push("/dashboard")}
                >
                  <Badge
                    color="#C9C9C9"
                    text={intl.formatMessage({ id: "goback" })}
                    fontSize="12px"
                  />
                </BadgeContainer>
                <BadgeContainer
                  onClick={() => formRef.current.submitForm()}
                >
                  <Badge
                    color="#002244"
                    text={intl.formatMessage({ id: "save" })}
                    fontSize="12px"
                    loading={loading}
                  />
                </BadgeContainer>
              </ActionContainer>
            </TitleContainer>
            <FieldsContainer>
              <Horizontal>
                <SelectWithLabel
                  name="event"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "event_edition" })}
                  placeholder={intl.formatMessage({ id: "event_edition" })}
                  labelOrientation="horizontal"
                  labelWidth="150px"
                  onChange={(event) => {
                    setSelectedEventCode(event?.code);
                  }}
                  options={events}
                  required
                />
              </Horizontal>
              <Horizontal>
                <SelectWithLabel
                  name="account"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "account" })}
                  placeholder={intl.formatMessage({ id: "account" })}
                  labelOrientation="horizontal"
                  labelWidth="150px"
                  onChange={(account) => {
                    setSelectedAccount(account?.value);
                  }}
                  options={accounts}
                  required
                />
              </Horizontal>
              <Horizontal>
                <SelectWithLabel
                  name="opportunity"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "opportunity" })}
                  placeholder={intl.formatMessage({ id: "opportunity" })}
                  labelOrientation="horizontal"
                  labelWidth="150px"
                  onChange={(opportunity) => {
                    setSelectedOpportunity(opportunity?.value);
                    setAllowedCredentialTypes(
                      opportunity?.allowedCredentialTypeList ?? null
                    );
                    formRef.current.setFieldValue("credentialType");
                  }}
                  options={opportunities}
                  required
                />
              </Horizontal>
              <Horizontal>
                <SelectWithLabel
                  name="serviceProvider"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "service_provider" })}
                  placeholder={intl.formatMessage({ id: "service_provider" })}
                  labelOrientation="horizontal"
                  labelWidth="150px"
                  options={serviceProviders}
                  clearable
                />
              </Horizontal>
              <Horizontal>
                <SelectWithLabel
                  name="credentialType"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({ id: "credential_types" })}
                  placeholder={intl.formatMessage({ id: "credential_types" })}
                  labelOrientation="horizontal"
                  labelWidth="150px"
                  options={filteredCredentialTypes}
                  disabled={!allowedCredentialTypes}
                  required
                />
              </Horizontal>
              <Horizontal>
                <InputWithHorizontalLabel
                  name="quantity"
                  placeholder={intl.formatMessage({ id: "quantity" })}
                  label={intl.formatMessage({ id: "quantity" })}
                  validationBlur={validationBlur}
                  labelWidth="150px"
                  required
                />
                {/*
                  INFORMACP-4819: os nomes dos campos seguem o payload da API (`laborUnion` e
                  `reason`), mas os rotulos passaram a ser "Motivo" e "Observacao".
                */}
                <SelectWithLabel
                  name="laborUnion"
                  validationBlur={validationBlur}
                  label={intl.formatMessage({
                    id: "reason",
                  })}
                  placeholder={intl.formatMessage({
                    id: "reason",
                  })}
                  labelOrientation="horizontal"
                  labelWidth="150px"
                  isMulti
                  options={laborUnions}
                  required
                />
              </Horizontal>
              <Horizontal>
                <TextareaWithLabel
                  name="reason"
                  label={intl.formatMessage({ id: "observation" })}
                  labelOrientation="horizontal"
                  labelWidth="150px"
                  validationBlur={validationBlur}
                  isHtml={true}
                  required
                />
              </Horizontal>
            </FieldsContainer>
          </Form>
        </FormContainer>
      </Container>

      <DelimiterContainer></DelimiterContainer>

      <Container>
        <Horizontal>
          <ExportContainer>
            <FormButton
              type="button"
              loading={loading}
              onClick={exportBonusCredentials}
            >
              <FormattedMessage id="export" />
            </FormButton>
          </ExportContainer>

          <SearchFormContainer>
            <Form>
              <FilterActionsContainer>
                <Input
                  name="search"
                  icon={AiOutlineSearch}
                  placeholder={intl.formatMessage({
                    id: "search"
                  })}
                  maxLength="255"
                  onChange={({ target }) => handleSearch(target.value)}
                />
              </FilterActionsContainer>
            </Form>
          </SearchFormContainer>
        </Horizontal>
      </Container>
      <Table
        columns={columns}
        data={data}
        progressPending={loading}
        paginationServer
        paginationTotalRows={totalRows}
        paginationDefaultPage={currentPage}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
      />
      <ConfirmationModal
        setIsOpen={setDeleteModalIsOpen}
        modalIsOpen={deleteModalIsOpen}
        confirmationFunction={handleExclusion}
        description={intl.formatMessage({ id: "exclusion_confirmation" })}
        confirmationText={intl.formatMessage({ id: "yes" })}
        cancelText={intl.formatMessage({ id: "no" })}
      />
    </AnimatedMainContainer>
  );
};

export default BonusCredential;
